import { Paper } from "@mui/material";
import styled, { createGlobalStyle, css } from "styled-components";

import { IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  deepPurpleMui,
  greenMui,
  greyMui,
  lightBlueMui,
  redMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

import {
  COURSES_CALENDAR_CELL_CHECKBOX_WIDTH_PX,
  COURSES_CALENDAR_CELL_EXPAND_POPPER_MAX_HEIGHT_PX,
  COURSES_CALENDAR_CELL_EXPAND_POPPER_WIDTH_PX,
  COURSES_CALENDAR_CELL_OUTER_PADDING_PX,
  COURSES_CALENDAR_CELL_ROW_HEIGHT_PX,
  COURSES_CALENDAR_CELL_SELECT_BAR_HEIGHT_PX,
  COURSES_CALENDAR_CELL_WIDTH_PX,
  COURSES_CALENDAR_HOUR_COLUMN_WIDTH_PX,
} from "./courses-calendar.constants";

const coursesCalendarCellCourseNameStyles = css`
  .cc-course-name-wrap {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
  }

  .cc-course-name-wrap [data-e2e="framer-tooltip"] {
    display: block;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .cc-course-name-cell {
    display: block;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.2;
    color: ${blueGreyMui._800};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cc-course-name-cell--link {
    color: ${deepPurpleMui.A700};

    &:hover {
      text-decoration: underline;
    }
  }
`;

/** Tooltip content can render outside `CoursesCalendarWrapperStyled` (portal). */
export const CoursesCalendarTooltipContentStyles = createGlobalStyle`
  .cc-ride-id-link {
    cursor: pointer;
    color: ${rydeColors.dragBorderColor};
    text-decoration: underline;
  }
  .cc-tooltip-course-line {
    font-size: 13px;
    font-weight: 500;
  }
  .cc-tooltip-course-title {
    font-size: 13px;
    font-weight: 500;
    margin: 0 0 3px;
  }
  .cc-tooltip-course-detail {
    font-size: 11px;
    font-weight: 300;
    margin: 0;
  }
  .cc-tooltip-course-meta {
    font-size: 11px;
    font-weight: 400;
    margin: 4px 0 0;
    color: inherit;
    opacity: 0.9;
  }

  .cc-cell-expand-popper {
    z-index: 1500;
  }
`;

export const CoursesCalendarCellExpandPopperPaper = styled(Paper)`
  width: ${COURSES_CALENDAR_CELL_EXPAND_POPPER_WIDTH_PX}px;
  max-height: ${COURSES_CALENDAR_CELL_EXPAND_POPPER_MAX_HEIGHT_PX}px;
  overflow: hidden;

  .cc-cell-expand-popper__inner {
    display: flex;
    flex-direction: column;
    max-height: ${COURSES_CALENDAR_CELL_EXPAND_POPPER_MAX_HEIGHT_PX}px;
  }

  .cc-cell-expand-popper__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 8px 8px 12px;
    border-bottom: 1px solid ${greyMui._200};
    flex-shrink: 0;
  }

  .cc-cell-expand-popper__title {
    font-size: 13px;
    font-weight: 600;
    color: ${blueGreyMui._800};
    line-height: 1.3;
    min-width: 0;
  }

  .cc-cell-expand-popper__close {
    flex-shrink: 0;
    color: ${blueGreyMui.A400};
  }

  .cc-cell-expand-popper__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 8px 12px 8px 10px;

    .cc-item {
      display: flex;
      flex-direction: column;
      gap: 2px;
      font-size: 10px;
      line-height: 1.15;
      border-radius: 8px;
      padding-block: 2px;
      padding-inline: 3px 4px;
      border: 1px solid transparent;
      min-width: 0;
      overflow: hidden;
      flex-shrink: 0;

      &:not(:last-child) {
        margin-bottom: 4px;
      }
    }

    .cc-item--not-created {
      background: ${greyMui._100};
      border-color: ${greyMui._400};
    }

    .cc-item--not-created-bookings {
      background: ${lightBlueMui._100};
      border-color: ${lightBlueMui._300};
    }

    .cc-item--active {
      background: ${greenMui._50};
      border-color: ${greenMui._300};
    }

    .cc-item--cancelled {
      background: ${redMui._50};
      border-color: ${redMui._200};
    }

    .cell_row {
      height: ${COURSES_CALENDAR_CELL_ROW_HEIGHT_PX}px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 2px;
      padding-inline: 2px 8px;
      min-width: 0;
      overflow: hidden;
      box-sizing: border-box;

      .cc-slot-select-cell {
        width: ${COURSES_CALENDAR_CELL_CHECKBOX_WIDTH_PX}px;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .direction_cell {
        width: 44px;
        flex-shrink: 0;
        display: flex;
      }

      .time_cell {
        font-size: 12px;
        width: 40px;
        flex-shrink: 0;
      }

      .cc-course-slot--editable {
        cursor: pointer;
      }

      .cc-ride-status-block {
        flex: 0 1 auto;
        min-width: 0;
        margin-inline-start: -3px;
      }

      ${coursesCalendarCellCourseNameStyles}

      .status_cell {
        font-size: 12px;
        flex: 0 0 auto;
        display: flex;
        align-items: center;

        .status_indicator {
          margin-inline-start: 0;
          font-size: 12px;
        }
      }

      .cc-status--interactive {
        cursor: pointer;
      }

      .cc-course-type-pill {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        padding: 0 4px;
        border-radius: 10px;
        background: ${lightBlueMui._50};
      }

      .cc-row-end {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
        margin-inline-start: auto;
      }

      .cc-passengers-slot {
        display: inline-flex;
        flex-shrink: 0;
      }

      .cc-passengers-pill {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        min-height: 18px;
        padding: 0 5px 0 3px;
        border-radius: 9px;
        line-height: 1;
        flex-shrink: 0;
        box-sizing: border-box;
      }

      .cc-passengers-pill__icon {
        width: 13px;
        height: 13px;
        flex-shrink: 0;
      }

      .cc-passengers-pill__ratio {
        display: inline-flex;
        align-items: baseline;
        gap: 0;
        line-height: 1;
      }

      .cc-passengers-pill__count {
        font-size: 12px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.02em;
      }

      .cc-passengers-pill__sep {
        font-size: 10px;
        font-weight: 500;
        opacity: 0.65;
        margin: 0 1px;
      }

      .cc-passengers--ride {
        background: ${greenMui._50};
        color: ${greenMui._800};
        border: 1px solid ${greenMui._200};
      }

      .cc-passengers--potential {
        background: ${deepPurpleMui._50};
        color: ${deepPurpleMui.A700};
        border: 1px solid ${deepPurpleMui._100};
      }

      .cc-passengers--empty {
        background: ${greyMui._100};
        color: ${blueGreyMui._700};
        border: 1px solid ${greyMui._300};
      }

      .actions_cell {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        width: auto;
        min-width: 22px;
        flex: 0 0 auto;
        color: ${blueGreyMui.A400};
        opacity: 1;
        pointer-events: auto;

        .cc-create-ride-icon {
          cursor: pointer;
          color: ${deepPurpleMui.A700};

          &:hover {
            color: ${deepPurpleMui.A400};
          }
        }
      }
    }
  }

  .cc-cell-expand-popper__footer {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    width: 100%;
    min-height: ${COURSES_CALENDAR_CELL_SELECT_BAR_HEIGHT_PX}px;
    padding: 4px 12px 6px;
    border-top: 1px solid ${greyMui._200};
    background: ${rydeColors.white};
    box-sizing: border-box;
    opacity: 1;
    pointer-events: auto;

    .cc-cell-footer-start {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
    }

    .cc-cell-footer-end {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin-inline-start: auto;
    }

    .cc-cell-selection-stat {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      flex-shrink: 0;
    }

    .cc-cell-selection-label {
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
      color: ${blueGreyMui._700};
      white-space: nowrap;
      user-select: none;
    }

    .cc-cell-selection-badge {
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
      padding: 2px 5px;
      border-radius: 8px;
      white-space: nowrap;
      user-select: none;
    }

    .cc-cell-selection-badge--total {
      color: ${blueGreyMui._700};
      background: ${greyMui._100};
      border: 1px solid ${greyMui._300};
    }

    .cc-cell-selection-badge--selected {
      color: ${deepPurpleMui.A700};
      background: ${deepPurpleMui._50};
      border: 1px solid ${deepPurpleMui._100};
    }

    .cc-cell-selection-badge--from {
      color: ${blueGreyMui._700};
      background: ${greyMui._100};
      border: 1px solid ${greyMui._300};
    }

    .cc-cell-select-link {
      font-size: 10px;
      font-weight: 500;
      line-height: 1.2;
      text-decoration: underline;
      color: ${deepPurpleMui.A700};
      cursor: pointer;
      border: none;
      background: transparent;
      padding: 0;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .cc-cell-select-link--deselect {
      color: ${redMui.A700};
    }
  }
`;

export const CoursesCalendarShellStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "courses-calendar-shell",
})`
  width: 100%;
  overflow: auto;
`;

export const CoursesCalendarWrapperStyled = styled.div<{
  $rowsCount: number;
  $daysCount: number;
}>`
  width: 100%;
  height: 100%;
  background: ${rydeColors.white};

  .head_dates_line {
    position: sticky;
    height: 40px;
    top: 0;
    z-index: 20;
    grid-row: fixedrow;
    grid-column-start: daycol 1;
    grid-column-end: daycol ${({ $daysCount }) => $daysCount + 1};
    display: flex;
    .renderIfVisible {
      width: ${COURSES_CALENDAR_CELL_WIDTH_PX}px;
    }
  }

  .fixed_pass_column {
    width: ${COURSES_CALENDAR_HOUR_COLUMN_WIDTH_PX}px;
    position: sticky;
    inset-inline-start: 0;
    z-index: 20;
    grid-row-start: passrow 1;
    grid-row-end: passrow ${({ $rowsCount }) => $rowsCount + 1};
    grid-column: fixedcol;
  }

  .date-col-wrapper {
    width: ${COURSES_CALENDAR_CELL_WIDTH_PX}px;
    grid-row-start: passrow 1;
    grid-row-end: passrow ${({ $rowsCount }) => $rowsCount + 1};
  }

  .cc-cal-cell.booking_cell {
    height: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    position: relative;
  }

  .booking_cell {
    background-color: ${rydeColors.white};
    border-inline-end: thin solid transparent;
    border-bottom: thin solid transparent;
    box-shadow:
      inset -1px 0 0 ${greyMui._300},
      inset 0 -1px 0 ${greyMui._300};
    padding: ${COURSES_CALENDAR_CELL_OUTER_PADDING_PX}px;
    font-size: 13px;
    overflow: hidden;
    position: relative;
    z-index: ${ScreenDepth.MAIN_CONTAINER};
    width: ${COURSES_CALENDAR_CELL_WIDTH_PX}px;

    &:hover {
      outline: 3px solid ${deepPurpleMui.A100};
      outline-offset: -3px;
    }

    &.cc_cal_cell__expand_open {
      outline: 3px solid ${deepPurpleMui.A100};
      outline-offset: -3px;
    }

    &.cc_cal_cell__has_slot_selection {
      outline: 1px solid ${deepPurpleMui._100};
      outline-offset: -1px;
    }

    &.cc_cal_cell__row_tint {
      background-color: ${deepPurpleMui._50};
    }

    .calendar_cell-dto_list {
      flex: 1 1 auto;
      min-height: 0;
      min-width: 0;
      overflow-x: hidden;
      overflow-y: auto;
      padding-bottom: 2px;

      .BlockHighlight {
        margin-inline-end: 4px;
      }
    }

    &:hover .calendar_cell-dto_list,
    &.cc_cal_cell__has_slot_selection .calendar_cell-dto_list,
    &.cc_cal_cell__has_overflow .calendar_cell-dto_list,
    &.cc_cal_cell__expand_open .calendar_cell-dto_list {
      padding-bottom: ${COURSES_CALENDAR_CELL_SELECT_BAR_HEIGHT_PX + 2}px;
    }

    .calendar_cell-footer {
      position: absolute;
      inset-inline: 0;
      bottom: 0;
      height: ${COURSES_CALENDAR_CELL_SELECT_BAR_HEIGHT_PX}px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 4px;
      width: 100%;
      padding-inline: 4px;
      background-color: ${rydeColors.white};
      border-top: 1px solid transparent;
      opacity: 0;
      pointer-events: none;
      transition: border-color 0.12s ease;
      z-index: 3;
    }

    &:hover .calendar_cell-footer,
    .calendar_cell-footer.cc-cell-footer--pinned,
    .calendar_cell-footer.cc-cell-footer--expand-open,
    .calendar_cell-footer.cc-cell-footer--always-visible {
      border-top-color: ${greyMui._200};
      opacity: 1;
      pointer-events: auto;
    }

    .cc-cell-expand-anchor {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
    }

    .cc-cell-footer-start {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
    }

    .cc-cell-footer-end {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin-inline-start: auto;
    }

    .cc-cell-selection-stat {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      flex-shrink: 0;
    }

    .cc-cell-selection-label {
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
      color: ${blueGreyMui._700};
      white-space: nowrap;
      user-select: none;
    }

    .cc-cell-overflow-count {
      display: none;
    }

    .cc-cell-selection-badge {
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
      padding: 2px 5px;
      border-radius: 8px;
      white-space: nowrap;
      user-select: none;
    }

    .cc-cell-selection-badge--total {
      color: ${blueGreyMui._700};
      background: ${greyMui._100};
      border: 1px solid ${greyMui._300};
    }

    .cc-cell-selection-badge--selected {
      color: ${deepPurpleMui.A700};
      background: ${deepPurpleMui._50};
      border: 1px solid ${deepPurpleMui._100};
    }

    .cc-cell-selection-badge--from {
      color: ${blueGreyMui._700};
      background: ${greyMui._100};
      border: 1px solid ${greyMui._300};
    }

    .cc-cell-expand-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      padding: 0;
      border: none;
      border-radius: 4px;
      background: transparent;
      color: ${deepPurpleMui.A700};
      cursor: pointer;
      flex-shrink: 0;

      &:hover {
        background: ${deepPurpleMui._50};
        color: ${deepPurpleMui.A400};
      }
    }

    .cc-cell-select-link {
      font-size: 10px;
      font-weight: 500;
      line-height: 1.2;
      text-decoration: underline;
      color: ${deepPurpleMui.A700};
      cursor: pointer;
      border: none;
      background: transparent;
      padding: 0;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .cc-cell-select-link--deselect {
      color: ${redMui.A700};
    }
  }

  .cell_pass_fixed {
    -webkit-font-smoothing: subpixel-antialiased;
    display: flex;
    align-items: center;
    width: ${COURSES_CALENDAR_HOUR_COLUMN_WIDTH_PX}px;
    box-sizing: border-box;
    padding-inline-start: 6px;
    padding-inline-end: 4px;
    background-color: ${rydeColors.white};
    border-inline-start: thin solid transparent;
    border-inline-end: thin solid transparent;
    border-bottom: thin solid transparent;
    box-shadow:
      inset 1px 0 0 ${greyMui._300},
      inset -1px 0 0 ${greyMui._300},
      inset 0 -1px 0 ${greyMui._300};
    will-change: transform;
    transform: scale(1);
    .checkbox {
      display: flex;
      align-items: center;
    }
  }

  .cell_pass_fixed.cc-hour-row--tint {
    background-color: ${deepPurpleMui._50};
  }

  .cc-hour-label-text {
    margin-inline-start: 4px;
    font-weight: 600;
    font-size: 12px;
    color: ${blueGreyMui._700};
    white-space: nowrap;
  }

  .head_cell {
    background-color: ${rydeColors.gridCellHover};
    color: ${blueGreyMui.A400};
    display: flex;
    align-items: center;
    padding-inline-start: 10px;
    padding-inline-end: 10px;
    border-inline-end: thin solid transparent;
    border-bottom: thin solid transparent;
    box-shadow:
      inset -1px 0 0 ${greyMui._300},
      inset 0 -1px 0 ${greyMui._300};
    font-size: 11px;
    width: ${COURSES_CALENDAR_CELL_WIDTH_PX}px;
    height: 40px;
    .checkbox {
      display: flex;
      align-items: center;
    }
    .checkbox_label {
      margin-inline-start: 5px;
    }
    .full_date {
      margin-inline-start: auto;
    }
  }

  .cell_row {
    height: ${COURSES_CALENDAR_CELL_ROW_HEIGHT_PX}px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 2px;
    padding-inline-end: 4px;
    min-width: 0;
    overflow: hidden;

    .cc-slot-select-cell {
      width: ${COURSES_CALENDAR_CELL_CHECKBOX_WIDTH_PX}px;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .direction_cell {
      width: 44px;
      flex-shrink: 0;
      display: flex;
    }
    .time_cell {
      font-size: 12px;
      width: 40px;
      flex-shrink: 0;
    }
    .cc-course-slot--editable {
      cursor: pointer;
    }
    .cc-ride-status-block {
      flex: 0 1 auto;
      min-width: 0;
      margin-inline-start: -3px;
    }
    ${coursesCalendarCellCourseNameStyles}
    .status_cell {
      font-size: 12px;
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      .status_indicator {
        margin-inline-start: 0;
        font-size: 12px;
      }
    }
    .cc-status--interactive {
      cursor: pointer;
    }
    .cc-course-type-pill {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      padding: 0 4px;
      border-radius: 10px;
      background: ${lightBlueMui._50};
    }
    .cc-row-end {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      flex-shrink: 0;
      margin-inline-start: auto;
    }
    .cc-passengers-slot {
      display: inline-flex;
      flex-shrink: 0;
    }
    .cc-passengers-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 2px;
      min-height: 18px;
      padding: 0 5px 0 3px;
      border-radius: 9px;
      line-height: 1;
      flex-shrink: 0;
      box-sizing: border-box;
    }
    .cc-passengers-pill__icon {
      width: 13px;
      height: 13px;
      flex-shrink: 0;
    }
    .cc-passengers-pill__ratio {
      display: inline-flex;
      align-items: baseline;
      gap: 0;
      line-height: 1;
    }
    .cc-passengers-pill__count {
      font-size: 12px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      letter-spacing: -0.02em;
    }
    .cc-passengers-pill__sep {
      font-size: 10px;
      font-weight: 500;
      opacity: 0.65;
      margin: 0 1px;
    }
    .cc-passengers--ride {
      background: ${greenMui._50};
      color: ${greenMui._800};
      border: 1px solid ${greenMui._200};
    }
    .cc-passengers--potential {
      background: ${deepPurpleMui._50};
      color: ${deepPurpleMui.A700};
      border: 1px solid ${deepPurpleMui._100};
    }
    .cc-passengers--empty {
      background: ${greyMui._100};
      color: ${blueGreyMui._700};
      border: 1px solid ${greyMui._300};
    }
    .actions_cell {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 2px;
      width: auto;
      min-width: 22px;
      flex: 0 0 auto;
      color: ${blueGreyMui.A400};
      opacity: 0;
      pointer-events: none;
      .cc-create-ride-icon {
        cursor: pointer;
        color: ${deepPurpleMui.A700};
        &:hover {
          color: ${deepPurpleMui.A400};
        }
      }
    }
    &:hover .actions_cell {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .cc-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 10px;
    line-height: 1.15;
    border-radius: 8px;
    padding-block: 2px;
    padding-inline: 3px 4px;
    border: 1px solid transparent;
    min-width: 0;
    overflow: hidden;
    flex-shrink: 0;

    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }

  .cc-item--slot-selected {
    box-shadow: inset 0 0 0 1px ${deepPurpleMui.A100};
  }

  .cc-item-row--actions {
    margin-top: 1px;
  }

  .cc-item--not-created {
    background: ${greyMui._100};
    border-color: ${greyMui._400};
  }

  .cc-item--not-created-bookings {
    background: ${lightBlueMui._100};
    border-color: ${lightBlueMui._300};
  }

  .cc-item--active {
    background: ${greenMui._50};
    border-color: ${greenMui._300};
  }

  .cc-item--cancelled {
    background: ${redMui._50};
    border-color: ${redMui._200};
  }

  .cc-item-row {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  .cc-first-cell {
    background-color: ${rydeColors.gridCellHover};
    display: flex;
    align-items: center;
    padding-inline-start: 8px;
    font-size: 11px;
    border-inline-start: thin solid transparent;
    border-inline-end: thin solid transparent;
    border-bottom: thin solid transparent;
    box-shadow:
      inset 1px 0 0 ${greyMui._300},
      inset -1px 0 0 ${greyMui._300},
      inset 0 -1px 0 ${greyMui._300};
    grid-column: fixedcol;
    grid-row: fixedrow;
    z-index: 25;
    .title {
      margin-inline-start: 4px;
    }
  }
`;
